import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Container, Form, Spinner } from "reactstrap";
import { Stack, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { TableC } from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCountry } from "@/Component/Form/SelectCountry/SelectCountry";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { usePagination } from "@/utils/hooks/usePagination";
import {
  useCampaignStatementReportMutation,
  useCampaignStatementReportQuery,
} from "@/services/reports";
import StatementReportFilters from "./StatementReportFilters";

const PAGE_SIZE = 10;

const pickText = (...values) =>
  values.find((value) => value != null && value !== "") ?? "-";

const pickNumber = (...values) => {
  const raw = values.find((value) => value != null && value !== "");
  if (raw == null || raw === "") return "-";
  const number = Number(raw);
  return Number.isFinite(number) ? number.toLocaleString() : raw;
};


const CampaignStatementReport = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [reportDataRes, setReportDataRes] = useState(null);
  const [shouldExport, setShouldExport] = useState(false);
  const [exportPayload, setExportPayload] = useState(null);
  const tableRef = useRef(null);

  const reportRows = reportDataRes?.data?.data || [];
  const meta = reportDataRes?.data?.meta || {};
  const totals = meta?.totals || {};

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      format: "json",
      orientation: "landscape",
      campaign_number: "",
      nationality_id: "",
      country_id: "",
      state_id: "",
      service_center_id: "",
      madina_service_center_id: "",
      visit: "",
      direction_id: "",
    },
  });

  const format = watch("format");
  const isPdf = format === "pdf";

  const { mutate: generateReport, isPending: isGenerating } =
    useCampaignStatementReportMutation();

  const {
    isFetching: isExporting,
    isSuccess: isExportSuccess,
    isError: isExportError,
    error: exportError,
  } = useCampaignStatementReportQuery(exportPayload, shouldExport);

  useEffect(() => {
    if (isExportSuccess) {
      toast.success(t("REPORT_EXPORTED_SUCCESSFULLY") || "Report exported successfully");
      setShouldExport(false);
      setExportPayload(null);
    }

    if (isExportError) {
      toast.error(
        exportError?.response?.data?.message ||
          t("REPORT_EXPORT_FAILED") ||
          "Failed to export report"
      );
      setShouldExport(false);
      setExportPayload(null);
    }
  }, [exportError, isExportError, isExportSuccess, t]);

  const fetchReportData = (payload, pageNum = 1) => {
    generateReport(
      {
        ...payload,
        page: pageNum,
        per_page: PAGE_SIZE,
      },
      {
        onSuccess: (response) => {
          setReportDataRes(response);
          setTimeout(() => {
            tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        },
      }
    );
  };

  const {
    page,
    setPage,
    hasSearched,
    handleSearch,
    handleReset: resetPagination,
  } = usePagination(fetchReportData, PAGE_SIZE);

  const onSubmit = (data) => {
    if (data.format === "json") {
      handleSearch(data);
      return;
    }

    setExportPayload(data);
    setShouldExport(true);
  };

  const columns = useMemo(
    () => [
      {
        name: t("MAKKAH_SERVICE_CENTER_ID"),
        key: "makkah_service_center",
        render: (_, row) =>
          pickText(
            row.makkah_service_center,
            row.service_center_name,
            getLocalizedText(row.service_center?.name_ar, row.service_center?.name_en),
            getLocalizedText(row.service_center?.nameAr, row.service_center?.nameEn),
            row.service_center?.name
          ),
      },
      {
        name: t("CAMPAIGN_NUMBER"),
        key: "campaign_number",
        render: (_, row) => pickText(row.campaign_number),
      },
      {
        name: t("CAMPAIGN_NAME"),
        key: "campaign_name",
        render: (_, row) => pickText(row.campaign_name, row.name),
      },
      {
        name: t("NATIONALITY"),
        key: "nationality",
        render: (_, row) =>
          row.nationality_display || row.nationality_name || row.nationality_label ? (
            pickText(row.nationality_display, row.nationality_name, row.nationality_label)
          ) : (
            <SelectCountry.Display value={row.nationality_id ?? row.nationality} />
          ),
      },
      {
        name: t("COUNTRY"),
        key: "country",
        render: (_, row) =>
          row.campaign_country_display || row.country_name || row.country_label ? (
            pickText(
              row.campaign_country_display,
              row.country_name,
              row.country_label
            )
          ) : (
            <SelectCountry.Display value={row.country_id ?? row.country} />
          ),
      },
      {
        name: t("STATE"),
        key: "state_name",
        render: (_, row) =>
          pickText(row.state_city, row.state_name, row.state?.name, row.state),
      },
      {
        name: t(
          "MADINA_SERVICE_CENTER_ID"
        ),
        key: "madina_service_center",
        render: (_, row) => pickText(row.madina_service_center),
      },
      {
        name: t("UPCOMING_COL_VISIT"),
        key: "visit",
        render: (_, row) => pickText(row.visit_label, row.visit_name, row.visit),
      },
      {
        name: t("EXPECTED_PILGRIMS_COUNT"),
        key: "expected_pilgrims",
        render: (_, row) =>
          pickNumber(row.expected_pilgrims, row.pilgrims_count, row.capacity),
      },
      {
        name: t("TRIPS_COUNT"),
        key: "trips_count",
        render: (_, row) =>
          pickNumber(row.trips_count, row.segments_count, row.trip_count),
      },
    ],
    [getLocalizedText, t]
  );

  const footerExpectedPilgrims =
    totals.expected_pilgrims ??
    reportRows.reduce(
      (sum, row) =>
        sum + Number(row.expected_pilgrims || row.pilgrims_count || row.capacity || 0),
      0
    );

  const isLoading = isGenerating || isExporting;

  const handleReset = () => {
    reset();
    resetPagination();
    setReportDataRes(null);
  };

  return (
    <Stack gap={3}>
      <Container fluid>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <StatementReportFilters
            control={control}
            errors={errors}
            type="campaign"
            isPdf={isPdf}
          />
          <ActionsFooter>
            <Button variant="primary" type="submit" disabled={isLoading} className="mt-3">
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("LOADING") || "Loading..."}
                </>
              ) : format === "json" ? (
                t("VIEW_REPORTS")
              ) : format === "pdf" ? (
                t("EXPORT_PDF")
              ) : (
                t("EXPORT_EXCEL")
              )}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="mt-3"
            >
              {t("RESET") || "Reset"}
            </Button>
          </ActionsFooter>
        </Form>
      </Container>

      {(hasSearched || reportRows.length > 0) && format === "json" && (
        <Container fluid>
          <TableC
            ref={tableRef}
            data={reportRows}
            columns={columns}
            loading={isGenerating}
            emptyStateTitle={t("NO_DATA") || "No data"}
            emptyStateDescription={t("NO_DATA_DESCRIPTION") || "No data found"}
            pagination={{
              current: page,
              total: meta?.total ?? 0,
              pageSize: meta?.per_page ?? PAGE_SIZE,
              onChange: setPage,
            }}
          />
          {reportRows.length > 0 && (
            <div className="fw-bold py-2 px-3 bg-white border rounded-bottom">
              {t("EXPECTED_PILGRIMS_COUNT")}: {pickNumber(footerExpectedPilgrims)}
            </div>
          )}
        </Container>
      )}
    </Stack>
  );
};

export default CampaignStatementReport;
